/**
 * NotFoundPage — 404 兜底页。
 *
 * 服务端（Worker spa-html）对未知路径已返回 404 + noindex 的 shell；
 * 客户端路由命中 `*`（App.tsx 顶层与 /:lang 下）时渲染本页，替代原先
 * "重定向回首页"的行为（重定向到首页会让爬虫把 404 当作 soft-404，
 * 也让用户丢失出错上下文）。
 *
 * 语言：优先从 URL 前缀 /:lang 解析（/zh/xxx 的 404 用中文文案），
 * 无前缀时回退 detectLang()。本页可能挂在 /:lang 之外，需自行同步
 * i18n 与 <html lang>（LangSync 只覆盖 /:lang 子树）。
 */

import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PublicLayout from "../components/PublicLayout";
import { LocalizedLink, detectLang, isSupportedLang } from "../lib/lang";
import type { Lang } from "../i18n";
import { setNoindex, clearNoindex } from "../lib/meta";

/** 从 pathname 提取语言前缀；无前缀/不支持时 null。 */
function langFromPath(pathname: string): Lang | null {
  const m = pathname.match(/^\/(en|zh|ja|ko)(\/|$)/);
  return m && isSupportedLang(m[1]) ? (m[1] as Lang) : null;
}

export default function NotFoundPage() {
  const { pathname } = useLocation();
  const { t, i18n } = useTranslation();
  const lang = useMemo(() => langFromPath(pathname) ?? detectLang(), [pathname]);

  useEffect(() => {
    // 404 页不可索引：服务端已注入 noindex，客户端路由进入时补齐同信号
    if (i18n.language !== lang) void i18n.changeLanguage(lang);
    document.documentElement.lang = lang;
    setNoindex();
    return () => {
      clearNoindex();
    };
  }, [lang, i18n]);

  return (
    <PublicLayout>
      <main className="notfound-page">
        <div className="notfound-code">404</div>
        <h1>{t("notFound.heading")}</h1>
        <p>{t("notFound.body")}</p>
        <LocalizedLink to="/" className="link-btn">
          {t("notFound.backHome")}
        </LocalizedLink>
      </main>
    </PublicLayout>
  );
}
